import { useState } from 'react';
import {
  Box, Grid, TextField, FormControl, InputLabel, Select, MenuItem,
  alpha, useTheme
} from '@mui/material';
import { Assignment as AssignmentIcon } from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import dayjs from 'dayjs';
import { SectionPaper, SectionTitle, StatusChip } from './styledComponents';

const DetailsSections = ({ invoice, onUpdate, canEdit = true }) => {
  const theme = useTheme();

  const fieldSx = {
    borderRadius: 3,
    border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
    background: 'white',
    opacity: canEdit ? 1 : 0.6,
    '& fieldset': { border: 'none' },
    '&:hover': canEdit ? { border: `1px solid ${theme.palette.primary.main}`, boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.15)}` } : {},
    '&.Mui-focused': canEdit ? { border: `1px solid ${theme.palette.primary.main}`, boxShadow: `0 2px 12px ${alpha(theme.palette.primary.main, 0.25)}` } : {}
  };

  const datePickerSx = {
    width: '100%',
    opacity: canEdit ? 1 : 0.6,
    '& .MuiOutlinedInput-root': {
      borderRadius: 3,
      border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
      background: 'white',
      '& fieldset': { border: 'none' }
    }
  };

  return (
    <SectionPaper sx={{ width: '100%', mt: 3 }}>
      <SectionTitle variant="h6"><AssignmentIcon />Invoice Details</SectionTitle>

      <Grid container spacing={3}>
        {/* Invoice Number */}
        <Grid item xs={12}>
          <TextField
            label="Invoice Number"
            value={invoice.invoice_number || ''}
            onChange={(e) => canEdit && onUpdate({ invoice_number: e.target.value })}
            fullWidth disabled={!canEdit}
            InputProps={{ sx: { ...fieldSx, opacity: canEdit ? 1 : 0.6 } }}
            helperText="Auto-generated — editable"
          />
        </Grid>

        {/* PI Reference Number */}
        <Grid item xs={12} sm={6}>
          <TextField
            label="Proforma Invoice Reference #"
            value={invoice.purchase_order_number || ''}
            onChange={(e) => canEdit && onUpdate({ purchase_order_number: e.target.value })}
            fullWidth disabled={!canEdit}
            placeholder="e.g. PI-202501-0001"
            InputProps={{ sx: fieldSx }}
          />
        </Grid>

        {/* BOL Reference Number */}
        <Grid item xs={12} sm={6}>
          <TextField
            label="Bill of Lading Reference #"
            value={invoice.bill_of_lading_number || ''}
            onChange={(e) => canEdit && onUpdate({ bill_of_lading_number: e.target.value })}
            fullWidth disabled={!canEdit}
            placeholder="e.g. BOL-202501-0001"
            InputProps={{ sx: fieldSx }}
          />
        </Grid>

        {/* Status */}
        <Grid item xs={12}>
          <FormControl fullWidth>
            <InputLabel>Status</InputLabel>
            <Select
              value={invoice.status || 'pending'}
              onChange={(e) => canEdit && onUpdate({ status: e.target.value })}
              label="Status"
              disabled={!canEdit}
              sx={{ borderRadius: 3, border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`, background: 'white', opacity: canEdit ? 1 : 0.6, '& .MuiOutlinedInput-notchedOutline': { border: 'none' } }}
            >
              {[
                { value: 'pending', label: 'Pending' },
                { value: 'draft', label: 'Draft' },
                { value: 'partial', label: 'Partial' },
                { value: 'paid', label: 'Paid' },
                { value: 'overdue', label: 'Overdue' },
                { value: 'cancelled', label: 'Cancelled' }
              ].map(({ value, label }) => (
                <MenuItem key={value} value={value}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <StatusChip label={label} status={value} size="small" />
                    {label}
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        {/* Invoice Date */}
        <Grid item xs={12} sm={6}>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DatePicker
              label="Invoice Date"
              value={invoice.invoice_date ? dayjs(invoice.invoice_date) : null}
              onChange={(newValue) => canEdit && onUpdate({ invoice_date: newValue ? newValue.format('YYYY-MM-DD') : '' })}
              disabled={!canEdit}
              sx={datePickerSx}
            />
          </LocalizationProvider>
        </Grid>

        {/* Due Date */}
        <Grid item xs={12} sm={6}>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DatePicker
              label="Due Date"
              value={invoice.due_date ? dayjs(invoice.due_date) : null}
              onChange={(newValue) => canEdit && onUpdate({ due_date: newValue ? newValue.format('YYYY-MM-DD') : '' })}
              disabled={!canEdit}
              sx={datePickerSx}
            />
          </LocalizationProvider>
        </Grid>
      </Grid>
    </SectionPaper>
  );
};

export default DetailsSections;
